import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Users, ArrowLeft, UserPlus } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Avatar } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import FollowButton from '../components/FollowButton';
import { useAuth } from '../App';

const BACKEND_URL = '';  // Use relative URLs
const API = '/api';

const FollowList = () => {
  const { handle } = useParams();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('followers');
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [followersPage, setFollowersPage] = useState(1);
  const [followingPage, setFollowingPage] = useState(1);
  const [hasMoreFollowers, setHasMoreFollowers] = useState(true);
  const [hasMoreFollowing, setHasMoreFollowing] = useState(true);

  useEffect(() => {
    fetchProfile();
  }, [handle]);

  useEffect(() => {
    if (profile) {
      if (activeTab === 'followers') {
        fetchFollowers();
      } else {
        fetchFollowing();
      }
    }
  }, [profile, activeTab, followersPage, followingPage]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/profile/${handle}`);
      setProfile(response.data);
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const fetchFollowers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/users/${profile.user_id}/followers?page=${followersPage}&limit=20`);
      
      if (followersPage === 1) {
        setFollowers(response.data.followers);
      } else {
        setFollowers(prev => [...prev, ...response.data.followers]);
      }
      
      setHasMoreFollowers(response.data.has_more);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching followers:', error);
      setLoading(false);
    }
  };

  const fetchFollowing = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/users/${profile.user_id}/following?page=${followingPage}&limit=20`);
      
      if (followingPage === 1) {
        setFollowing(response.data.following);
      } else {
        setFollowing(prev => [...prev, ...response.data.following]);
      }
      
      setHasMoreFollowing(response.data.has_more);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching following:', error);
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const UserCard = ({ userProfile }) => {
    const isOwnProfile = user?.id === userProfile.user_id;

    return (
      <Card className="bg-gray-900 border-gray-800 hover:border-gray-700 transition-colors">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            {/* Avatar */}
            <Link to={`/u/${userProfile.handle}`}>
              <Avatar className="w-16 h-16 bg-gray-800">
                {userProfile.avatar_url ? (
                  <img src={userProfile.avatar_url} alt={userProfile.display_name} />
                ) : (
                  <div className="flex items-center justify-center w-full h-full text-gray-400 text-xl">
                    {userProfile.display_name?.charAt(0).toUpperCase()}
                  </div>
                )}
              </Avatar>
            </Link>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <Link to={`/u/${userProfile.handle}`} className="block">
                <h3 className="font-bold text-lg hover:text-orange-500 transition-colors truncate">
                  {userProfile.display_name}
                </h3>
                <p className="text-sm text-gray-400 truncate">@{userProfile.handle}</p>
              </Link>
              
              {userProfile.bio && (
                <p className="text-sm text-gray-300 mt-1 line-clamp-2">{userProfile.bio}</p>
              )}
              
              <div className="flex items-center gap-2 mt-2">
                {userProfile.is_creator && userProfile.creator_tier && (
                  <Badge className="bg-orange-500/10 text-orange-400 text-xs">
                    {userProfile.creator_tier}
                  </Badge>
                )}
                {userProfile.followed_at && (
                  <span className="text-xs text-gray-500">
                    Followed {formatDate(userProfile.followed_at)}
                  </span>
                )}
              </div>
            </div>

            {/* Follow Button */}
            {!isOwnProfile && (
              <FollowButton
                targetUserId={userProfile.user_id}
                targetHandle={userProfile.handle}
                size="sm"
              />
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  if (!profile) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="bg-gray-900 border-b border-gray-800">
        <div className="container mx-auto px-4 sm:px-6 py-6">
          <Link to={`/u/${handle}`}>
            <Button variant="ghost" className="mb-4 text-gray-400 hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Profile
            </Button>
          </Link>
          
          <div className="flex items-center gap-4">
            <Avatar className="w-16 h-16 bg-gray-800">
              {profile.avatar_url ? (
                <img src={profile.avatar_url} alt={profile.display_name} />
              ) : (
                <div className="flex items-center justify-center w-full h-full text-gray-400 text-xl">
                  {profile.display_name?.charAt(0).toUpperCase()}
                </div>
              )}
            </Avatar>
            
            <div>
              <h1 className="text-2xl font-bold">{profile.display_name}</h1>
              <p className="text-gray-400">@{profile.handle}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 py-8">
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-gray-900 border border-gray-800 mb-6">
            <TabsTrigger value="followers" className="data-[state=active]:bg-orange-500">
              <Users className="w-4 h-4 mr-2" />
              Followers
              {followers.length > 0 && ` (${followers.length})`}
            </TabsTrigger>
            <TabsTrigger value="following" className="data-[state=active]:bg-orange-500">
              <UserPlus className="w-4 h-4 mr-2" />
              Following
              {following.length > 0 && ` (${following.length})`}
            </TabsTrigger>
          </TabsList>

          {/* Followers Tab */}
          <TabsContent value="followers">
            {loading && followersPage === 1 ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
              </div>
            ) : followers.length === 0 ? (
              <Card className="bg-gray-900 border-gray-800">
                <CardContent className="p-12 text-center">
                  <Users className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-xl font-bold mb-2">No followers yet</h3>
                  <p className="text-gray-400">
                    {user?.id === profile.user_id 
                      ? 'Share your profile to get followers!'
                      : 'Be the first to follow!'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {followers.map((follower) => (
                  <UserCard key={follower.user_id} userProfile={follower} />
                ))}
                
                {hasMoreFollowers && (
                  <div className="text-center py-6">
                    <Button
                      onClick={() => setFollowersPage(prev => prev + 1)}
                      variant="outline"
                      className="border-gray-800 hover:bg-gray-800"
                      disabled={loading}
                    >
                      {loading ? 'Loading...' : 'Load More'}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          {/* Following Tab */}
          <TabsContent value="following">
            {loading && followingPage === 1 ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
              </div>
            ) : following.length === 0 ? (
              <Card className="bg-gray-900 border-gray-800">
                <CardContent className="p-12 text-center">
                  <UserPlus className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-xl font-bold mb-2">Not following anyone</h3>
                  <p className="text-gray-400">
                    {user?.id === profile.user_id
                      ? 'Discover creators to follow!'
                      : `${profile.display_name} hasn't followed anyone yet.`}
                  </p>
                  {user?.id === profile.user_id && (
                    <Link to="/browse">
                      <Button className="mt-4 bg-orange-500 hover:bg-orange-600">
                        Browse Creators
                      </Button>
                    </Link>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {following.map((followedUser) => (
                  <UserCard key={followedUser.user_id} userProfile={followedUser} />
                ))}
                
                {hasMoreFollowing && (
                  <div className="text-center py-6">
                    <Button
                      onClick={() => setFollowingPage(prev => prev + 1)}
                      variant="outline"
                      className="border-gray-800 hover:bg-gray-800"
                      disabled={loading}
                    >
                      {loading ? 'Loading...' : 'Load More'}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default FollowList;
